import type { WeatherScene as SceneName } from '@td/shared';

/**
 * The little animated picture of what the sky is doing.
 *
 * Drawn as SVG with CSS animation rather than pulled from an icon set: it is
 * a few hundred bytes, it takes the palette's colours, and it can actually
 * move. A sun that turns and clouds that drift are the difference between a
 * dashboard that feels alive and one that feels like a spreadsheet.
 *
 * Every animation is switched off under `prefers-reduced-motion` — the styles
 * for that live in index.css, next to the keyframes.
 */

const SUN = '#D9A85C';
const MOON = '#DCCDBB';
const CLOUD = '#C9CFD8';
const CLOUD_DARK = '#9BA5B4';
const RAIN = '#7887A0';
const SNOW = '#E8EEF4';
const BOLT = '#E0B03C';

export function WeatherScene({
  scene,
  isDay = true,
  size = 96,
}: {
  scene: SceneName;
  isDay?: boolean;
  size?: number;
}) {
  return (
    <svg
      viewBox="0 0 120 100"
      width={size}
      height={size * (100 / 120)}
      role="img"
      aria-hidden
      className="wx"
      style={{ overflow: 'visible' }}
    >
      {scene === 'clear' && (isDay ? <Sun /> : <Moon />)}

      {scene === 'partly' && (
        <>
          {isDay ? <Sun cx={44} cy={36} r={17} /> : <Moon cx={44} cy={36} r={15} />}
          <Cloud x={16} y={16} />
        </>
      )}

      {scene === 'cloudy' && (
        <>
          <Cloud x={4} y={4} scale={0.8} fill={CLOUD_DARK} opacity={0.55} drift="wx-drift-slow" />
          <Cloud x={12} y={14} />
        </>
      )}

      {scene === 'fog' && (
        <>
          <Cloud x={12} y={6} opacity={0.75} />
          {[0, 1, 2].map((i) => (
            <rect
              key={i}
              x={18}
              y={68 + i * 9}
              width={84}
              height={5}
              rx={2.5}
              fill={CLOUD}
              opacity={0.6}
              className={`wx-fog wx-fog-${i}`}
            />
          ))}
        </>
      )}

      {(scene === 'drizzle' || scene === 'rain' || scene === 'showers') && (
        <>
          {scene === 'showers' && isDay && <Sun cx={94} cy={22} r={13} />}
          <Cloud x={12} y={10} fill={scene === 'rain' ? CLOUD_DARK : CLOUD} />
          <Drops
            count={scene === 'drizzle' ? 4 : scene === 'rain' ? 7 : 5}
            length={scene === 'drizzle' ? 7 : 13}
          />
        </>
      )}

      {scene === 'sleet' && (
        <>
          <Cloud x={12} y={10} fill={CLOUD_DARK} />
          <Drops count={4} length={10} />
          <Flakes count={3} offset={22} />
        </>
      )}

      {scene === 'snow' && (
        <>
          <Cloud x={12} y={10} />
          <Flakes count={6} />
        </>
      )}

      {scene === 'storm' && (
        <>
          <Cloud x={12} y={8} fill={CLOUD_DARK} />
          <Drops count={4} length={11} />
          {/* Two bolts on different beats, so it flickers rather than blinks. */}
          <path d="M62 60l-12 20h9l-5 16 16-22h-9l6-14z" fill={BOLT} className="wx-bolt" />
          <path
            d="M40 64l-8 14h6l-4 11 11-15h-6l4-10z"
            fill={BOLT}
            opacity={0.75}
            className="wx-bolt wx-bolt-2"
          />
        </>
      )}
    </svg>
  );
}

function Sun({ cx = 60, cy = 44, r = 20 }: { cx?: number; cy?: number; r?: number }) {
  return (
    <g>
      {/* Rays turn; the disc does not, or the whole thing looks like a wheel. */}
      <g className="wx-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
        {Array.from({ length: 8 }, (_, i) => (
          <rect
            key={i}
            x={cx - 1.6}
            y={cy - r - 11}
            width={3.2}
            height={8}
            rx={1.6}
            fill={SUN}
            opacity={0.85}
            transform={`rotate(${i * 45} ${cx} ${cy})`}
          />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={r} fill={SUN} className="wx-pulse" />
      <circle cx={cx} cy={cy} r={r} fill="#F2C879" opacity={0.5} />
    </g>
  );
}

function Moon({ cx = 60, cy = 44, r = 19 }: { cx?: number; cy?: number; r?: number }) {
  return (
    <g>
      {/* A crescent cut from two circles — no mask, no filter, no fuss. */}
      <path
        d={`M${cx + r * 0.35} ${cy - r} a${r} ${r} 0 1 0 ${r * 0.5} ${r * 1.7} a${r * 0.85} ${r * 0.85} 0 1 1 ${-r * 0.5} ${-r * 1.7}z`}
        fill={MOON}
        className="wx-pulse"
      />
      {[
        [cx - 30, cy - 22, 2.2],
        [cx + 26, cy - 28, 1.6],
        [cx + 32, cy + 8, 2],
        [cx - 24, cy + 18, 1.4],
      ].map(([x, y, size], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={size}
          fill="#F7F3ED"
          className={`wx-twinkle wx-twinkle-${i}`}
        />
      ))}
    </g>
  );
}

function Cloud({
  x = 12,
  y = 10,
  scale = 1,
  fill = CLOUD,
  opacity = 1,
  drift = 'wx-drift',
}: {
  x?: number;
  y?: number;
  scale?: number;
  fill?: string;
  opacity?: number;
  drift?: string;
}) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity} className={drift}>
      <circle cx={30} cy={40} r={16} fill={fill} />
      <circle cx={52} cy={32} r={21} fill={fill} />
      <circle cx={74} cy={42} r={15} fill={fill} />
      <rect x={28} y={40} width={48} height={16} rx={8} fill={fill} />
    </g>
  );
}

function Drops({ count, length }: { count: number; length: number }) {
  return (
    <g>
      {Array.from({ length: count }, (_, i) => (
        <line
          key={i}
          x1={30 + i * 12}
          y1={62}
          x2={27 + i * 12}
          y2={62 + length}
          stroke={RAIN}
          strokeWidth={3}
          strokeLinecap="round"
          className={`wx-drop wx-drop-${i % 5}`}
        />
      ))}
    </g>
  );
}

function Flakes({ count, offset = 0 }: { count: number; offset?: number }) {
  return (
    <g>
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          cx={30 + offset + i * 11}
          cy={66}
          r={3}
          fill={SNOW}
          stroke={CLOUD}
          strokeWidth={0.8}
          className={`wx-flake wx-flake-${i % 5}`}
        />
      ))}
    </g>
  );
}
